import "./App.css";
import "font-awesome/css/font-awesome.min.css";
import "@fontsource/ysabeau-sc";
import { Helmet } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useParams, Navigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserRoles } from "./redux/authSlice";
import { useHasPrivilege, useHasAdminAccess } from "./hooks/usePermissions";

// Components
import MainPage from "./components/mainPage/mainPage";
import RegistrationForm from "./components/Registration/RegistrationForm";
import Menu from "./components/Menu/Menu";
import AdminPage from "./components/AdminPage/AdminPage";
import LoginPage from "./components/LoginPage/LoginPage";
import SuccessRegistration from "./components/Registration/SuccessRegistration";
import PrivateRoute from "./components/PrivateRoute";
import Library from "./components/Library/Library";
import DocumentsPage from "./components/DocumentsPage/DocumentsPage";
import AdminBooks from "./components/AdminPage/AdminBooks/AdminBooks";
import ContributionsPage from "./components/AdminPage/ContributionsPage/ContributionsPage";
import ExpensesPage from "./components/AdminPage/ExpensesPage/ExpensesPage";
import PrintDocsPage from "./components/AdminPage/PrintDocsPage/PrintDocsPage";
import Events from "./components/AdminPage/Events/Events";
import Presentation from "./components/mainPage/Presentation/Presentation";
import ProfilePage from "./components/ProfilePage/ProfilePage";
import Organizations from "./components/AdminPage/Organizations/Organizations";
import RoleManagement from "./components/AdminPage/Organizations/management/RoleManagement";
import AcceptMembersPage
  from "./components/AdminPage/Members/AcceptMembersPage";
import MembersPage from "./components/AdminPage/Members/MembersPage";
import ManageEvents from "./components/AdminPage/Events/ManageEvents";
import AccessManagement from "./components/AdminPage/access-management/AccessManagement";
import RolesManagement from "./components/AdminPage/Roles/RolesManagement";
import AnnouncementManagement from "./components/AdminPage/Announcement/AnnouncementManagement";
import ArticleViewPage from "./components/mainPage/NewsPreview/Articles/ArticleViewPage";
import ArticleEditor from "./components/AdminPage/News/ArticleEditor";
import StatReportForm from "./components/StatReportForm/StatReportForm";
import StatReportsPage from "./components/AdminPage/StatReportsPage/StatReportsPage";
import StatReportsSummaryPage from "./components/AdminPage/StatReportsPage/StatReportsSummaryPage";
import Contacts from "./components/Contacts/Contacts";
import ContactsManagement from "./components/AdminPage/Contacts/ContactsManagement";

const AdminRoute = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const hasAdminAccess = useHasAdminAccess();
  return isAuthenticated && hasAdminAccess ? (
      children
  ) : (
      <Navigate to="/login" />
  );
};

const MenuOrNull = () => {
  const location = useLocation();
  return !location.pathname.startsWith("/admin") ? <Menu /> : null;
};

const AdminHome = () => {
  const hasPrivilege = useHasPrivilege();
  const sections = [
    { path: "organizations", privilege: "MANAGE:ORGANIZATIONS" },
    { path: "members", privilege: "MANAGE:MEMBERS" },
    { path: "accept-members", privilege: "MANAGE:MEMBERS" },
    { path: "access-management", privilege: "MANAGE:ACCESS" },
    { path: "roles", privilege: "MANAGE:ROLES" },
    { path: "print-docs", privilege: "MANAGE:PRINTDOCS" },
    { path: "events", privilege: "MANAGE:EVENTS" },
    { path: "books", privilege: "MANAGE:BOOKS" },
    { path: "news", privilege: "MANAGE:NEWS" },
    { path: "articles", privilege: "MANAGE:ARTICLES" },
    { path: "contributions", privilege: "MANAGE:FINANCE" },
    { path: "expenses", privilege: "MANAGE:FINANCE" },
    { path: "stat-reports", privilege: "MANAGE:FINANCE" },
  ];
  const target = sections.find((section) => hasPrivilege(section.privilege));
  return target
      ? <Navigate to={target.path} replace />
      : <div>Нет доступных разделов</div>;
};

// Перечитываем роли и привилегии при каждом переходе по страницам,
// чтобы изменения прав применялись в меню без ожидания ротации токена.
function RoleRefreshOnNavigate() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const location = useLocation();
  const skipFirstRun = useRef(true);

  useEffect(() => {
    if (skipFirstRun.current) {
      skipFirstRun.current = false;
      return;
    }
    if (!isAuthenticated) return;
    // Редиректы /admin -> /admin/<section> идут сразу друг за другом:
    // сбрасываем таймер, чтобы /api/users/profile уходил один раз на навигацию.
    const handler = setTimeout(() => {
      dispatch(fetchUserRoles());
    }, 200);
    return () => clearTimeout(handler);
  }, [dispatch, isAuthenticated, location.pathname]);

  return null;
}

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      // Подтягиваем актуальные роли и привилегии с сервера
      // (patched fetch сам обновит access-токен, если тот протух)
      dispatch(fetchUserRoles());
    }
  }, [dispatch, isAuthenticated]);

  // Перечитываем роли, когда пользователь возвращается на вкладку
  // (например, после смены ролей админом в другом окне)
  useEffect(() => {
    if (!isAuthenticated) return;
    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') {
        dispatch(fetchUserRoles());
      }
    };
    window.addEventListener('focus', refreshOnVisible);
    document.addEventListener('visibilitychange', refreshOnVisible);
    return () => {
      window.removeEventListener('focus', refreshOnVisible);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, [dispatch, isAuthenticated]);

  return (
      <div className="App">
        <Helmet>
          <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
          />
        </Helmet>

        <BrowserRouter>
          <RoleRefreshOnNavigate />
          <MenuOrNull />
          <div className="content-area">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<MainPage />} />
              <Route path="/library" element={<Library />} />
              <Route path="/registration" element={<RegistrationForm />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/documents" element={<DocumentsPage />} />
              <Route path="/about/contacts" element={<Contacts />} />
              <Route path="/about/organization" element={<Presentation />} />
              <Route path="/registrationSuccess" element={<SuccessRegistration />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/articles/:id" element={<ArticleViewPage />} />
              <Route path="/stat-report" element={<StatReportForm />} />

              {/* Admin Routes */}
              <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AdminPage />
                    </AdminRoute>
                  }
              >
                <Route index element={<AdminHome />} />
                <Route path="organizations" element={
                  <PrivateRoute requiredPrivilege="MANAGE:ORGANIZATIONS">
                    <Organizations />
                  </PrivateRoute>
                } />
                <Route path="organizations/:id/roles" element={
                  <PrivateRoute requiredPrivilege="MANAGE:ROLES">
                    <RoleManagementRoute />
                  </PrivateRoute>
                } />
                <Route path="books" element={
                  <PrivateRoute requiredPrivilege="MANAGE:BOOKS">
                    <AdminBooks />
                  </PrivateRoute>
                } />
                <Route path="contributions" element={
                  <PrivateRoute requiredPrivilege="MANAGE:FINANCE">
                    <ContributionsPage />
                  </PrivateRoute>
                } />
                <Route path="expenses" element={
                  <PrivateRoute requiredPrivilege="MANAGE:FINANCE">
                    <ExpensesPage />
                  </PrivateRoute>
                } />
                <Route path="stat-reports" element={
                  <PrivateRoute requiredPrivilege="MANAGE:FINANCE">
                    <StatReportsPage />
                  </PrivateRoute>
                } />
                <Route path="stat-reports/summary" element={
                  <PrivateRoute requiredPrivilege="MANAGE:FINANCE">
                    <StatReportsSummaryPage />
                  </PrivateRoute>
                } />
                <Route path="print-docs" element={
                  <PrivateRoute requiredPrivilege="MANAGE:PRINTDOCS">
                    <PrintDocsPage />
                  </PrivateRoute>
                } />
                <Route path="events" element={
                  <PrivateRoute requiredPrivilege="MANAGE:EVENTS">
                    <Events />
                  </PrivateRoute>
                } />
                <Route path="manage-events" element={
                  <PrivateRoute requiredPrivilege="MANAGE:EVENTS">
                    <ManageEvents />
                  </PrivateRoute>
                } />
                <Route path="accept-members" element={
                  <PrivateRoute requiredPrivilege="MANAGE:MEMBERS">
                    <AcceptMembersPage />
                  </PrivateRoute>
                } />
                <Route path="members" element={
                  <PrivateRoute requiredPrivilege="MANAGE:MEMBERS">
                    <MembersPage />
                  </PrivateRoute>
                } />
                <Route path="access-management" element={
                  <PrivateRoute requiredPrivilege="MANAGE:ACCESS">
                    <AccessManagement />
                  </PrivateRoute>
                } />
                <Route path="roles" element={
                  <PrivateRoute requiredPrivilege="MANAGE:ROLES">
                    <RolesManagement />
                  </PrivateRoute>
                } />
                <Route path="contacts" element={
                  <PrivateRoute requiredPrivilege="MANAGE:CONTACTS">
                    <ContactsManagement />
                  </PrivateRoute>
                } />
                <Route path="articles" element={
                  <PrivateRoute requiredPrivilege="MANAGE:ARTICLES">
                    <ArticleEditor />
                  </PrivateRoute>
                } />
                <Route path="news" element={
                  <PrivateRoute requiredPrivilege="MANAGE:NEWS">
                    <AnnouncementManagement />
                  </PrivateRoute>
                } />
              </Route>
            </Routes>
          </div>
        </BrowserRouter>
      </div>
  );
}

export default App;

// Route wrapper to inject :id as prop to RoleManagement
function RoleManagementRoute() {
  const params = useParams();
  return <RoleManagement organizationId={params.id} />;
}